import { Controller } from "@hotwired/stimulus";
import { effect } from "@preact/signals-core";
import { uuid } from "../live/uuid";
import { combatBridge } from "../maps/combat_bridge";
import { canMove, canTurn, initials, isFrameless, isKeyMovable, isOwn, RING_COLORS, ringKey, sheetLink } from "../maps/tokens";
import { lengthOf, parseDecimal, validSize, withLength, withWidth } from "../maps/transform";
import type MapBoard from "./map_board_controller";
import type MapSheet from "./map_sheet_controller";

const GAP = 12;
const NAME_WRITE_MS = 600;
// Tailwind's max-sm: below it the menu is a bottom sheet, placed by CSS.
const SHEET = "(max-width: 639px)";

// The words the menu composes in script, from the server (_board.html.erb).
type Labels = {
	group_hint: string;
	group_hint_multi: string;
	group_pieces: string;
	group_remove: string;
	group_visible: string;
	move: string;
	move_anon: string;
	move_frozen: string;
	move_off: string;
	move_on: string;
	notice: { frozen: string; locked: string };
	snap_mixed: string;
	snap_off: string;
	snap_on: string;
	subtitle: Record<string, string>;
	visible_off: string;
	visible_on: string;
};

// Connects to data-controller="map-token-menu", beside map-board on the board
// root. The board opens this small menu beside a selected token: *Ângulo* and
// the sheet link for whoever may use them and, for the master, everything
// else (*Largura* and *Comprimento* in place of *Tamanho* on frameless
// pieces), each switch and button writing through the board at once, a number
// field on change and the name once typing pauses. The header shows the
// piece's avatar and what it is; docs/architecture/maps.md, "Selection and menu".
export default class extends Controller {
	static values = { labels: Object };
	static targets = [
		"avatar",
		"avatarText",
		"avatarImage",
		"subtitle",
		"notice",
		"noticeText",
		"moveLabel",
		"ownerNote",
		"ringToggle",
		"ringPanel",
		"ringName",
		"ringCurrent",
		"menu",
		"title",
		"fields",
		"sheet",
		"panelSheet",
		"name",
		"hidden",
		"locked",
		"snap",
		"size",
		"sizeField",
		"width",
		"angle",
		"turnKeys",
		"span",
		"framelessField",
		"handles",
		"color",
		"ringGroup",
		"single",
		"group",
		"groupAvatars",
		"groupTitle",
		"nameField",
		"groupVisible",
		"groupSnap",
		"groupRemove",
		"multi",
	];
	declare readonly labelsValue: Labels;
	declare readonly avatarTarget: HTMLElement;
	declare readonly avatarTextTarget: HTMLElement;
	declare readonly avatarImageTarget: HTMLImageElement;
	declare readonly subtitleTarget: HTMLElement;
	declare readonly noticeTarget: HTMLElement;
	declare readonly noticeTextTarget: HTMLElement;
	declare readonly moveLabelTarget: HTMLElement;
	declare readonly ownerNoteTarget: HTMLElement;
	declare readonly ringToggleTarget: HTMLElement;
	declare readonly hasRingToggleTarget: boolean;
	declare readonly ringPanelTarget: HTMLElement;
	declare readonly ringNameTarget: HTMLElement;
	declare readonly ringCurrentTarget: HTMLElement;
	declare readonly menuTarget: HTMLElement;
	declare readonly titleTarget: HTMLElement;
	declare readonly fieldsTarget: HTMLElement;
	declare readonly sheetTarget: HTMLAnchorElement;
	declare readonly panelSheetTarget: HTMLElement;
	declare readonly hasPanelSheetTarget: boolean;
	declare readonly nameTarget: HTMLInputElement;
	declare readonly hasNameTarget: boolean;
	declare readonly hiddenTarget: HTMLElement;
	declare readonly lockedTarget: HTMLElement;
	declare readonly snapTarget: HTMLElement;
	declare readonly sizeTarget: HTMLInputElement;
	declare readonly hasSizeTarget: boolean;
	declare readonly sizeFieldTarget: HTMLElement;
	declare readonly widthTarget: HTMLInputElement;
	declare readonly angleTarget: HTMLInputElement;
	declare readonly turnKeysTarget: HTMLElement;
	declare readonly spanTarget: HTMLInputElement;
	declare readonly framelessFieldTargets: HTMLElement[];
	declare readonly handlesTarget: HTMLElement;
	declare readonly hasHandlesTarget: boolean;
	declare readonly colorTargets: HTMLElement[];
	declare readonly ringGroupTarget: HTMLElement;
	declare readonly singleTarget: HTMLElement;
	declare readonly hasGroupTarget: boolean;
	declare readonly groupTarget: HTMLElement;
	declare readonly groupAvatarsTarget: HTMLElement;
	declare readonly groupTitleTarget: HTMLElement;
	declare readonly nameFieldTarget: HTMLElement;
	declare readonly groupVisibleTarget: HTMLElement;
	declare readonly groupSnapTarget: HTMLElement;
	declare readonly groupRemoveTarget: HTMLElement;
	declare readonly multiTarget: HTMLElement;
	avatarsKey?: string;
	tokenId?: string | null;
	sheetPath?: string | null;
	dispose?: () => void;
	nameTimer?: ReturnType<typeof setTimeout>;
	// One undo step for the renames typed while the name field keeps focus.
	nameSession?: string | null;
	// The group's menu hides the name field, which then holds a piece that is not the group's: it commits nothing.
	groupShown?: boolean;
	framelessSet?: Set<string>;
	stickerSet?: Set<string>;

	disconnect() {
		this.close();
	}

	// Built once: framelessValue never changes once the board connects.
	get frameless() {
		if (!this.framelessSet) this.framelessSet = new Set(this.board.framelessValue);
		return this.framelessSet;
	}

	get stickers() {
		if (!this.stickerSet) this.stickerSet = new Set(this.board.stickersValue);
		return this.stickerSet;
	}

	get board() {
		return this.application.getControllerForElementAndIdentifier(this.element, "map-board") as MapBoard | null;
	}

	get sheetPanel() {
		return this.application.getControllerForElementAndIdentifier(this.element, "map-sheet") as MapSheet | null;
	}

	open(id: string) {
		const { state } = this.board;
		this.close();
		this.tokenId = id;
		this.menuTarget.hidden = false;
		if (this.hasRingToggleTarget) this.openRing(false);
		this.dispose = effect(() => {
			const token = state.view.value.tokens?.[id];
			const selection = state.local.selection.value;
			state.local.viewport.value;
			state.local.drafts.value;
			state.local.handles.value;
			if (!token) return queueMicrotask(() => this.close());
			const group = this.groupOf(selection);
			if (group.length > 1) this.fillGroup(group);
			else this.fill(token);
			this.place();
		});
		[...this.menuTarget.querySelectorAll("button")].find((button) => !button.closest("[hidden]"))?.focus({ preventScroll: true });
	}

	close() {
		if (!this.tokenId) return;
		this.commitName();
		this.nameSession = null;
		this.dispose?.();
		this.dispose = null;
		this.tokenId = null;
		this.menuTarget.hidden = true;
		// Focus goes back to the board the token is drawn on.
		if (this.menuTarget.contains(document.activeElement)) this.board?.canvasTarget.focus({ preventScroll: true });
	}

	dismiss() {
		this.close();
	}

	openSheet() {
		const path = this.sheetPath;
		const id = this.tokenId;
		const inFight = this.inFight(id);
		this.close();
		if (inFight && id) combatBridge.value?.openSheet(id);
		else if (path) this.sheetPanel?.open(path);
	}

	inFight(id: string | null | undefined) {
		return Boolean(id && this.board?.state.local.combat.value[id]?.monster);
	}

	// One step per Escape: a handle drag, then the handles, then the menu.
	escape(event?: KeyboardEvent) {
		// A field of the PV block drops what was typed on its own Escape; the next one closes.
		if (event?.defaultPrevented) return;
		if (this.board?.renderer?.cancelTransform?.()) return;
		if (!this.board?.hideHandles()) this.close();
	}

	// The selected pieces the group menu is about: two or more on the master's board, those still on the scene.
	groupOf(selection: string[]) {
		if (!this.hasGroupTarget || selection.length < 2) return [];
		const { tokens } = this.board.state.view.peek();
		return selection.map((id) => tokens?.[id]).filter(Boolean);
	}

	// The group's menu in place of the piece's, or back.
	showGroup(on: boolean) {
		if (!this.hasGroupTarget) return;
		this.singleTarget.hidden = on;
		this.groupTarget.hidden = !on;
		this.groupAvatarsTarget.hidden = !on;
		this.groupTitleTarget.hidden = !on;
		this.avatarTarget.hidden = on;
		this.nameFieldTarget.hidden = on;
	}

	// The count, up to three stacked rings, the visible switch (on while any piece shows) with its tally, and the snap switch, on while every piece snaps.
	fillGroup(pieces) {
		const labels = this.labelsValue;
		const count = pieces.length;
		this.showGroup(true);
		clearTimeout(this.nameTimer);
		this.nameSession = null;
		this.groupShown = true;
		const title = labels.group_pieces.replace("%{count}", String(count));
		this.titleTarget.textContent = title;
		this.groupTitleTarget.textContent = title;
		this.subtitleTarget.textContent = this.board.state.local.multi.value ? labels.group_hint_multi : labels.group_hint;
		this.paintAvatars(pieces);
		const shown = pieces.filter((token) => !token.hidden).length;
		this.setSwitch(this.groupVisibleTarget, shown > 0, labels.group_visible.replace("%{shown}", String(shown)).replace("%{count}", String(count)));
		const snapping = pieces.filter((token) => token.snap !== false).length;
		this.setSwitch(this.groupSnapTarget, snapping === count, snapping === count ? labels.snap_on : snapping === 0 ? labels.snap_off : labels.snap_mixed);
		const remove = this.groupRemoveTarget.querySelector("span");
		if (remove) remove.textContent = labels.group_remove.replace("%{count}", String(count));
	}

	// The first pieces' rings and initials, overlapping; a "+" stands for the rest when there are more than three.
	paintAvatars(pieces) {
		const shown = pieces.slice(0, 3).map((token) => ({
			ring: RING_COLORS[ringKey(token)],
			text: initials(token.name || this.board.unlabeledTitlesValue[token.prefab] || ""),
		}));
		if (pieces.length > 3) shown[2] = { ring: shown[2].ring, text: "+" };
		const key = JSON.stringify(shown);
		if (key === this.avatarsKey) return;
		this.avatarsKey = key;
		this.groupAvatarsTarget.replaceChildren(
			...shown.map(({ ring, text }) => {
				const bubble = document.createElement("span");
				bubble.className =
					"w-[30px] h-[30px] rounded-full border-[3px] bg-surface-sunken flex items-center justify-center text-xs font-extrabold text-ink -ml-3 first:ml-0";
				bubble.style.borderColor = ring;
				bubble.textContent = text;
				return bubble;
			}),
		);
	}

	fill(token) {
		this.showGroup(false);
		this.groupShown = false;
		const title = token.name || this.board.unlabeledTitlesValue[token.prefab] || "";
		this.titleTarget.textContent = title;
		const { viewer, state } = this.board;
		const scene = state.view.peek().scene;
		const frameless = isFrameless(token, this.frameless);
		this.paintAvatar(token, title, frameless);
		this.subtitleTarget.textContent = this.subtitle(token);
		this.fieldsTarget.hidden = !canTurn(token, viewer, scene);
		// Q and E turn only a piece the keys move (`isKeyMovable`); an object turns by its handle or this field.
		this.turnKeysTarget.hidden = !isKeyMovable(token);
		this.show(this.angleTarget, token.rotation ?? 0);
		const link = sheetLink(token, viewer);
		// A sheet the panel can host, or a monster of the fight, opens there; the panel carries the way to the full sheet.
		const inPanel = Boolean(link) && this.hasPanelSheetTarget && (this.inFight(token.id) || Boolean(this.sheetPanel?.has(link)));
		if (this.hasPanelSheetTarget) this.panelSheetTarget.hidden = !inPanel;
		this.sheetTarget.hidden = !link || inPanel;
		if (link) this.sheetTarget.href = link;
		this.sheetPath = link;
		// A player is told why their own piece will not move; a frozen scene wins over a locked piece.
		const why = !viewer.manage && isOwn(token, viewer) && !canMove(token, viewer, scene) ? (scene?.movement_frozen ? "frozen" : "locked") : null;
		this.noticeTarget.hidden = !why;
		if (why) this.noticeTextTarget.textContent = this.labelsValue.notice[why];
		if (!viewer.manage) return;
		if (document.activeElement !== this.nameTarget) this.nameTarget.value = token.label ?? "";
		this.nameTarget.placeholder = title;
		const labels = this.labelsValue;
		this.setSwitch(this.hiddenTarget, !token.hidden, token.hidden ? labels.visible_off : labels.visible_on);
		// Only a piece with an owner has a move switch; the master moves the rest alone.
		const owned = Boolean(token.owner_account_id);
		this.lockedTarget.hidden = !owned;
		this.ownerNoteTarget.hidden = owned;
		if (owned) {
			const name = this.board.participant(token.owner_account_id)?.name;
			this.moveLabelTarget.textContent = name ? labels.move.replace("%{owner}", name) : labels.move_anon;
			this.setSwitch(this.lockedTarget, !token.locked, token.locked ? labels.move_off : scene?.movement_frozen ? labels.move_frozen : labels.move_on);
		}
		const snaps = token.snap !== false;
		this.setSwitch(this.snapTarget, snaps, snaps ? labels.snap_on : labels.snap_off);
		this.sizeFieldTarget.hidden = frameless;
		for (const field of this.framelessFieldTargets) field.hidden = !frameless;
		this.showShape(token);
		this.handlesTarget.hidden = token.kind !== "prop";
		this.handlesTarget.setAttribute("aria-checked", String(state.local.handles.peek() === token.id));
		this.multiTarget.setAttribute("aria-pressed", String(state.local.multi.value));
		const ring = ringKey(token);
		for (const button of this.colorTargets) {
			const current = button.dataset.color === ring;
			button.setAttribute("aria-pressed", String(current));
			if (current) this.showRing(ring, button);
		}
		this.ringGroupTarget.hidden = frameless;
	}

	// The ring colour in the header's avatar and in the disclosure row, whose swatch is the palette's own.
	showRing(ring: string, button: HTMLElement) {
		this.ringNameTarget.textContent = button.getAttribute("aria-label");
		if (this.ringCurrentTarget.dataset.color === ring) return;
		this.ringCurrentTarget.dataset.color = ring;
		this.ringCurrentTarget.innerHTML = button.innerHTML;
	}

	// The picture clipped round, or the initials, in the ring colour; a rounded square with no ring when frameless.
	paintAvatar(token, title: string, frameless: boolean) {
		const url = token.image_url;
		const image = this.avatarImageTarget;
		image.hidden = !url;
		if (url && image.getAttribute("src") !== url) image.setAttribute("src", url);
		this.avatarTextTarget.textContent = url ? "" : initials(title);
		this.avatarTarget.toggleAttribute("data-frameless", frameless);
		this.avatarTarget.style.borderColor = frameless ? "" : RING_COLORS[ringKey(token)];
	}

	// What the piece is: its kind, and whose when it has an owner; a player sees only that it is theirs.
	subtitle(token) {
		const { viewer } = this.board;
		const words = this.labelsValue.subtitle;
		if (!viewer.manage && isOwn(token, viewer)) return words.own;
		const owner = this.board.participant(token.owner_account_id)?.name;
		const named = (kind: string) => (owner ? words[`${kind}_of`].replace("%{owner}", owner) : words[kind]);
		if (token.kind === "sheet") return named(token.retainer_id ? "retainer" : "character");
		if (token.kind === "monster") return words.monster;
		if (token.kind === "marker") return words.marker;
		return this.stickers.has(token.image_url) ? words.sticker : words.prop;
	}

	// A switch row: its state, and the hint under its label.
	setSwitch(button: HTMLElement, on: boolean, hint: string) {
		button.setAttribute("aria-checked", String(on));
		const line = button.querySelector("[data-hint]");
		if (line) line.textContent = hint;
	}

	// A field being typed in keeps its text.
	show(field: HTMLInputElement, value: number) {
		if (document.activeElement !== field) this.restore(field, value);
	}

	// Leaving a field shows what the piece has: a turn the model kept modulo
	// 360, a value dropped, or "1.50" as "1,5".
	settle() {
		const token = this.token;
		if (!token) return;
		this.show(this.angleTarget, token.rotation ?? 0);
		if (this.hasSizeTarget) this.showShape(token);
	}

	showShape(token: { size?: number; span?: number | null }) {
		this.show(this.sizeTarget, token.size ?? 1);
		this.show(this.widthTarget, token.size ?? 1);
		this.show(this.spanTarget, lengthOf(token));
	}

	restore(field: HTMLInputElement, value: number) {
		field.value = new Intl.NumberFormat(document.documentElement.lang || undefined, { maximumFractionDigits: 4, useGrouping: false }).format(value);
	}

	// Beside the token, flipped to its left or clamped when the board runs out;
	// positions are the frame's, which keeps clear of the screen's notches.
	place() {
		const token = this.board.state.view.peek().tokens[this.tokenId];
		const { step, group } = this.board.state.local.drafts.peek();
		const at = this.board.renderer?.imageToClient(step?.id === this.tokenId ? { ...token, ...step } : { ...token, ...group?.[this.tokenId] });
		const board = (this.menuTarget.offsetParent ?? this.element).getBoundingClientRect();
		if (!at || window.matchMedia(SHEET).matches) return;
		const menu = this.menuTarget.getBoundingClientRect();
		const zoom = this.board.state.local.viewport.peek().zoom;
		const extents = this.board.renderer?.tokenExtents(this.tokenId);
		const radius = (extents?.x ?? ((token.size ?? 1) * this.cellSide()) / 2) * zoom;
		let left = at.x - board.left + radius + GAP;
		if (left + menu.width > board.width - GAP) left = at.x - board.left - radius - GAP - menu.width;
		left = Math.min(Math.max(left, GAP), Math.max(board.width - menu.width - GAP, GAP));
		const top = Math.min(Math.max(at.y - board.top - menu.height / 2, GAP), Math.max(board.height - menu.height - GAP, GAP));
		this.menuTarget.style.left = `${left}px`;
		this.menuTarget.style.top = `${top}px`;
	}

	cellSide() {
		const { grid } = this.board.state.view.peek().scene;
		return Math.min(grid.cell_w, grid.cell_h);
	}

	// The board may leave the page before this menu does.
	get token() {
		return this.board?.state.view.peek().tokens?.[this.tokenId];
	}

	queueName() {
		clearTimeout(this.nameTimer);
		this.nameTimer = setTimeout(() => this.commitName(), NAME_WRITE_MS);
	}

	commitName() {
		clearTimeout(this.nameTimer);
		const token = this.token;
		if (!token || !this.hasNameTarget || this.groupShown) return;
		this.nameSession ??= uuid();
		const session = this.nameSession;
		if (document.activeElement !== this.nameTarget) this.nameSession = null;
		const label = this.nameTarget.value.trim() || null;
		if (label !== (token.label ?? null)) this.board.renameToken(this.tokenId, label, session);
	}

	// Written as typed; a value that is not a number, or a size the model
	// refuses, goes back to what the piece has.
	resize() {
		const token = this.token;
		if (!token) return;
		const size = parseDecimal(this.sizeTarget.value);
		if (!validSize(size)) return this.restore(this.sizeTarget, token.size ?? 1);
		if (size !== (token.size ?? 1)) this.board.resizeToken(this.tokenId, size);
	}

	// A width in cells with the length kept, so the span changes with it; a
	// blank one, or one the model refuses, goes back to what the piece has.
	widen() {
		const token = this.token;
		if (!token) return;
		const shape = withWidth(token, parseDecimal(this.widthTarget.value));
		if (!shape) return this.restore(this.widthTarget, token.size ?? 1);
		if (shape.size !== (token.size ?? 1)) this.board.reshapeToken(this.tokenId, shape.size, shape.span);
	}

	// A length in cells with the width kept; cleared, it is the width, which
	// is square and written as no span.
	stretch() {
		const token = this.token;
		if (!token) return;
		const text = this.spanTarget.value.trim();
		const shape = withLength(token, text ? parseDecimal(text) : (token.size ?? 1));
		if (!shape) return this.restore(this.spanTarget, lengthOf(token));
		if (shape.span !== (token.span ?? null)) this.board.stretchToken(this.tokenId, shape.span);
	}

	rotate() {
		const token = this.token;
		if (!token) return;
		const rotation = parseDecimal(this.angleTarget.value);
		if (!Number.isFinite(rotation)) return this.restore(this.angleTarget, token.rotation ?? 0);
		this.board.rotateToken(this.tokenId, rotation);
	}

	toggleHandles() {
		this.board.toggleHandles();
	}

	toggleMulti() {
		this.board.toggleMulti();
	}

	// The group's switches and buttons write to every selected piece, as one step.
	get group() {
		return this.groupOf(this.board.state.local.selection.peek());
	}

	// What V does: hides every piece when any shows, else shows them all.
	toggleGroupVisible() {
		this.board.toggleHiddenSelected();
	}

	toggleGroupSnap() {
		const { group } = this;
		this.board.updateTokens(
			group.map(({ id }) => id),
			{ snap: !group.every((token) => token.snap !== false) },
		);
	}

	// The group's pieces, or the one this menu is open on.
	bringFront() {
		this.board.layerTokens(this.layered, "front");
	}

	sendBack() {
		this.board.layerTokens(this.layered, "back");
	}

	get layered() {
		const { group } = this;
		return group.length > 1 ? group.map(({ id }) => id) : [this.tokenId];
	}

	toggleHidden() {
		this.board.updateToken(this.tokenId, { hidden: !this.token.hidden });
	}

	toggleLocked() {
		this.board.updateToken(this.tokenId, { locked: !this.token.locked });
	}

	toggleSnap() {
		this.board.updateToken(this.tokenId, { snap: this.token.snap === false });
	}

	recolor({ currentTarget }: Event & { currentTarget: HTMLElement }) {
		this.board.updateToken(this.tokenId, { ring_color: currentTarget.dataset.color || null });
		// The swatch is about to be hidden, and focus goes with it.
		this.ringToggleTarget.focus({ preventScroll: true });
		this.openRing(false);
		this.place();
	}

	toggleRing() {
		this.openRing(this.ringToggleTarget.getAttribute("aria-expanded") !== "true");
		// The palette changes the menu's height, so near the board's edge it must be placed again.
		this.place();
	}

	openRing(open: boolean) {
		this.ringToggleTarget.setAttribute("aria-expanded", String(open));
		this.ringPanelTarget.hidden = !open;
	}

	remove() {
		const { group } = this;
		if (group.length > 1) this.board.removeTokens(group.map(({ id }) => id));
		else this.board.removeToken(this.tokenId);
	}
}
